import {
  createMiniappTguidMissingSender,
  createUmamiSendWindow,
  type ReportMiniappTguidMissing,
} from '@agnivo/app-core';
import { appConfig } from '../config';
import { reportClientEvent } from './clientEventReporter';

/**
 * ADR-082 mini-app tg_uid-missing reopen surface (architect ruling
 * `decisions/architect/2026-09-14-miniapp-tguid-counters-one-event-with-a-step-dimension-no-token-self-serve.md`).
 * Built ONCE as a module-level singleton, not via `useState`/`useAppConfig()` like
 * app-core's shell-agnostic senders (`RouteViewReporter`, `ApiProvider`'s funnel
 * sender): those read config through React context because app-core is
 * surface-agnostic and does not know its shell at import time. Mini-app's own
 * `appConfig` is already a static, eagerly-evaluated module constant (`config.ts`)
 * imported directly everywhere in this app — there is no per-render reactivity to
 * gain from threading this through React, and both call sites (a class method and
 * a plain DOM handler) are equally well served by a plain function reference.
 *
 * `copyVersion: 'v1'` — ADR-082 observability ruling item 3: starts at `'v1'`,
 * bump when the reopen copy in `launchCopy.ts`'s `terminal.tgUidMissing` changes.
 *
 * Never throws: mirrors `buildRouteViewSender`'s guard in
 * `packages/app-core/src/telemetry/RouteViewReporter.tsx` — `createMiniappTguidMissingSender`
 * calls `buildUrl` internally, which throws on a malformed `collectorUrl` (one bad
 * env var away). A throw at MODULE EVAL time here would break every consumer that
 * transitively imports this file, which is strictly worse than the render-time
 * throw that guard exists to prevent — so the same try/catch applies, one level
 * earlier.
 */
const NO_OP: ReportMiniappTguidMissing = () => {};

function build(): ReportMiniappTguidMissing {
  const analytics = appConfig.analytics;
  if (analytics === undefined) return NO_OP;
  try {
    return createMiniappTguidMissingSender({
      apiMode: appConfig.apiMode,
      collectorUrl: analytics.collectorUrl,
      websiteId: analytics.websiteId,
      clientShell: appConfig.clientShell,
      hostname: analytics.hostname,
      copyVersion: 'v1',
      // Reports through the mini-app's own module-scope Class-1 reporter
      // (`clientEventReporter.ts`) — closes the gap this file previously flagged
      // as tracked-not-silent (`backlog/done/2026-09-18-frontend-mini-app-has-no-
      // class-1-telemetry-reporter.md`).
      umamiWindow: createUmamiSendWindow({ report: reportClientEvent }),
    });
  } catch {
    return NO_OP;
  }
}

export const reportTguidMissing: ReportMiniappTguidMissing = build();
